import React, { useEffect, useState } from "react";
import Toast from "../../components/common/toast/Toast";
import InfoMessage from "../../components/common/message/InfoMessage";
import { User } from "../../types/types";
import { deleteUser, getAllusers } from "./userController";
import { Search } from "lucide-react";
import DeleteButton from "../../components/common/buttons/DeleteButton";

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`bg-white shadow-md rounded-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardContent: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

const UserList: React.FC = () => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | "warning">(
    "success",
  );

  const handleDeleteUser = async (userId: number) => {
    try {
      const result = await deleteUser(userId);
      if (result.success) {
        // Remove the deleted user from the state
        setUsers(users.filter((user) => user.id !== userId));
        setFilteredUsers(filteredUsers.filter((user) => user.id !== userId));

        // Show success message
        setToastMessage("User deleted successfully!");
        setToastType("success");
        setShowToast(true);
      } else {
        setToastMessage("Failed to delete user");
        setToastType("error");
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage("An error occurred while deleting the user");
      setToastType("error");
      setShowToast(true);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      console.log("Fetching users");
      const fetchedUsers = await getAllusers();
      setUsers(fetchedUsers);
      setFilteredUsers(fetchedUsers);
      setIsProcessing(false);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const results = users.filter(
      (user) =>
        `${user.firstName || ""} ${user.lastName || ""}`
          .toLowerCase()
          .includes(lowerSearchTerm) ||
        // ✅ FIX: Added fallbacks (|| "") to prevent crashes when email or contact is null
        (user.email || "").toLowerCase().includes(lowerSearchTerm) ||
        (user.contact || "").toLowerCase().includes(lowerSearchTerm),
    );
    setFilteredUsers(results);
  }, [searchTerm, users]);

  return (
    <div className="container mx-auto p-4">
      {showToast && (
        <Toast
          type={toastType}
          onClose={() => setShowToast(false)}
          message={toastMessage}
        />
      )}
      <h2 className="text-3xl font-bold mb-9">User Management</h2>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-800">
              All Users{" "}
              <span className="text-indigo-600">
                ({filteredUsers.length || 0})
              </span>
            </h3>
            <div className="relative">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isProcessing ? (
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="h-10 bg-slate-200 rounded"></div>
              ))}
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">
                      Name
                    </th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">
                      Email
                    </th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">
                      Contact
                    </th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">
                      Created At
                    </th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4">{`${user.firstName || ""} ${user.lastName || ""}`}</td>
                      {/* ✅ FIX: Mask dummy emails/contacts in the UI for a cleaner look */}
                      <td className="py-3 px-4">
                        {user.email?.startsWith("guest_") ? (
                          <span className="text-gray-400 italic">No Email</span>
                        ) : (
                          user.email
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {user.contact?.startsWith("no-contact") ? (
                          <span className="text-gray-400 italic">
                            No Contact
                          </span>
                        ) : (
                          user.contact
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <DeleteButton
                          onConfirm={() => handleDeleteUser(user.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <InfoMessage message="No users found. Please add new users or try a different search term." />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserList;
